import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles } from "lucide-react";
import { api } from "../lib/api";
import { Carousel, CarouselContent, CarouselItem } from "./ui/carousel";

export const promoHref = (p) => {
  if (p.link_type === "product" && p.link_value) return `/product/${p.link_value}`;
  if (p.link_type === "category" && p.link_value) return `/shop/${encodeURIComponent(p.link_value)}`;
  return "/shop";
};

const fmtEnd = (d) => new Date(`${d}T00:00:00`).toLocaleDateString("en-IN", { day: "numeric", month: "short" });

function PromoCard({ p }) {
  return (
    <Link to={promoHref(p)} className="group relative flex flex-col md:flex-row md:h-full min-h-[200px] md:min-h-[230px] overflow-hidden rounded-[22px]"
      style={{ background: "#0D0C0A", color: "#F5EFDF", boxShadow: "0 24px 50px -28px rgba(13,12,10,0.55)" }} data-testid={`promo-card-${p.id}`}>
      <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(120% 90% at 0% 0%, rgba(201,154,42,0.28), transparent 55%)" }}></div>
      <div className="relative z-[1] flex-1 p-5 md:p-8 flex flex-col">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.28em]" style={{ color: "#E8D68C", fontFamily: "'JetBrains Mono', monospace" }}>
          <Sparkles className="w-3 h-3" /> <span className="whitespace-nowrap">Offer · till {fmtEnd(p.end_date)}</span>
        </div>
        {p.discount_label && (
          <div className="mt-3 font-display text-[28px] md:text-5xl leading-none" style={{ background: "linear-gradient(180deg,#F3DC8C,#B08616)", WebkitBackgroundClip: "text", color: "transparent" }} data-testid={`promo-discount-${p.id}`}>
            {p.discount_label}
          </div>
        )}
        <div className="mt-3 font-display text-xl md:text-2xl leading-tight" data-testid={`promo-title-${p.id}`}>{p.title}</div>
        {p.description && <p className="mt-2 text-sm leading-relaxed line-clamp-2" style={{ color: "rgba(245,239,223,0.65)" }}>{p.description}</p>}
        <span className="mt-auto pt-5 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] font-semibold" style={{ color: "#E8D68C" }}>
          Shop the offer <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
        </span>
      </div>
      {p.image && (
        <div className="relative order-first md:order-none h-32 md:h-auto md:w-[38%] shrink-0 overflow-hidden" data-testid={`promo-image-${p.id}`}>
          <img src={p.image} alt={p.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
          <div className="absolute inset-0 md:hidden" style={{ background: "linear-gradient(0deg,#0D0C0A 0%,rgba(13,12,10,0) 60%)" }}></div>
          <div className="absolute inset-0 hidden md:block" style={{ background: "linear-gradient(90deg,#0D0C0A 0%,rgba(13,12,10,0) 45%)" }}></div>
        </div>
      )}
    </Link>
  );
}

export default function PromoSlider() {
  const [promos, setPromos] = useState([]);
  const [embla, setEmbla] = useState(null);
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);
  const [snaps, setSnaps] = useState(0);

  useEffect(() => { api.get("/promotions/active").then((r) => setPromos(r.data)).catch(() => setPromos([])); }, []);

  useEffect(() => {
    if (!embla) return;
    const onSelect = () => setCurrent(embla.selectedScrollSnap());
    const onInit = () => { setSnaps(embla.scrollSnapList().length); onSelect(); };
    onInit();
    embla.on("select", onSelect).on("reInit", onInit);
    return () => { embla.off("select", onSelect); embla.off("reInit", onInit); };
  }, [embla]);

  useEffect(() => {
    if (!embla || paused || snaps < 2) return;
    const t = setInterval(() => embla.scrollNext(), 4500);
    return () => clearInterval(t);
  }, [embla, paused, snaps]);

  if (promos.length === 0) return null;
  const multi = promos.length > 1;

  return (
    <section className="container-x pt-8 md:pt-12" data-testid="promo-section" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
      <Carousel opts={{ loop: multi, align: "start" }} setApi={setEmbla}>
        <CarouselContent>
          {promos.map((p) => (
            <CarouselItem key={p.id} className={multi ? "basis-[86%] sm:basis-[70%] md:basis-1/2" : "basis-full"}><PromoCard p={p} /></CarouselItem>
          ))}
        </CarouselContent>
      </Carousel>
      {snaps > 1 && (
        <div className="mt-4 flex items-center justify-center gap-2" data-testid="promo-dots">
          {Array.from({ length: snaps }).map((_, i) => (
            <button key={i} onClick={() => embla?.scrollTo(i)} aria-label={`Show offer ${i + 1}`}
              className="h-1.5 rounded-full transition-[width,background-color] duration-300"
              style={{ width: i === current ? 28 : 8, background: i === current ? "#8A6A0F" : "rgba(15,12,8,0.2)" }} data-testid={`promo-dot-${i}`} />
          ))}
        </div>
      )}
    </section>
  );
}
